import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Tournament, TournamentMatch, TournamentTeam } from '../../types';
import { Trophy, Crown, Flame, ChevronRight, Edit3, Award, Sparkles, CheckCircle2, AlertCircle } from 'lucide-react';
import { triggerHaptic } from '../../utils/haptics';

interface VisualTournamentBracketProps {
  tournament: Tournament;
  onEditMatchScore?: (match: TournamentMatch) => void;
  onSelectTeam?: (team: TournamentTeam) => void;
  onNominateMvp?: (match: TournamentMatch) => void;
}

export const VisualTournamentBracket: React.FC<VisualTournamentBracketProps> = ({
  tournament,
  onEditMatchScore,
  onSelectTeam,
  onNominateMvp,
}) => {
  const matches = tournament.matches || [];
  const [selectedMatchId, setSelectedMatchId] = useState<string | null>(null);

  // Group matches by round
  const roundsMap = matches.reduce((acc, m) => {
    const r = m.round || 1;
    if (!acc[r]) acc[r] = [];
    acc[r].push(m);
    return acc;
  }, {} as Record<number, TournamentMatch[]>);

  const roundNumbers = Object.keys(roundsMap).map(Number).sort((a, b) => a - b);

  // Find overall champion
  const finalMatch = matches.find((m) => m.round === Math.max(...roundNumbers) || m.roundName?.toLowerCase().includes('final'));
  const championTeam = finalMatch?.winnerId
    ? (finalMatch.winnerId === finalMatch.team1?.id ? finalMatch.team1 : finalMatch.team2)
    : null;

  const getRoundLabel = (r: number, totalRounds: number) => {
    if (r === totalRounds) return '🏆 CHAMPIONSHIP FINALS';
    if (r === totalRounds - 1) return '⚡ SEMIFINALS';
    if (r === totalRounds - 2) return '🔥 QUARTERFINALS';
    return `ROUND ${r}`;
  };

  return (
    <div className="bg-slate-950/90 p-5 md:p-8 rounded-[2.5rem] border border-white/10 shadow-2xl space-y-6 relative overflow-x-auto custom-scrollbar">
      {/* HEADER & BRACKET LEGEND */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-white/10 pb-4">
        <div>
          <div className="flex items-center space-x-2">
            <span className="px-2.5 py-0.5 rounded-full bg-lime-400 text-black text-[10px] font-black uppercase font-mono">
              VISUAL MATCH PROGRESSION BRACKET
            </span>
            <span className="text-xs text-indigo-300 font-bold">• {tournament.format}</span>
          </div>
          <h3 className="text-xl md:text-2xl font-black italic uppercase text-white mt-1 flex items-center gap-2">
            <span>{tournament.title || 'Tournament Bracket'}</span>
            <Trophy className="w-5 h-5 text-amber-400" />
          </h3>
        </div>

        {/* Legend */}
        <div className="flex items-center space-x-3 text-[10px] font-extrabold uppercase font-mono text-indigo-200 bg-indigo-950 p-2 rounded-xl border border-white/10">
          <div className="flex items-center space-x-1">
            <span className="w-2.5 h-2.5 rounded-full bg-lime-400" />
            <span>Winner Advanced</span>
          </div>
          <div className="flex items-center space-x-1">
            <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
            <span>Live / In Progress</span>
          </div>
          <div className="flex items-center space-x-1">
            <span className="w-2.5 h-2.5 rounded-full bg-indigo-600" />
            <span>Scheduled</span>
          </div>
        </div>
      </div>

      {/* BRACKET TREE COLUMNS */}
      {roundNumbers.length === 0 ? (
        <div className="p-8 text-center text-xs text-indigo-300 italic bg-indigo-950/60 rounded-2xl border border-white/10">
          No matches scheduled for this tournament yet.
        </div>
      ) : (
        <div className="flex items-start gap-8 min-w-[780px] pb-4 pt-2 relative">
          {roundNumbers.map((roundNum, rIdx) => {
            const roundMatches = roundsMap[roundNum] || [];
            const isFinalRound = rIdx === roundNumbers.length - 1;

            return (
              <div key={roundNum} className="flex-1 flex flex-col space-y-4 shrink-0 min-w-[240px] relative">
                {/* Round Title Header */}
                <div className="bg-indigo-900/80 p-2.5 rounded-xl border border-white/10 text-center shadow">
                  <span className="text-[11px] font-black italic uppercase text-lime-400 tracking-wider block">
                    {getRoundLabel(roundNum, roundNumbers.length)}
                  </span>
                  <span className="text-[9px] font-mono text-indigo-300 block mt-0.5">
                    {roundMatches.length} {roundMatches.length === 1 ? 'Match' : 'Matches'}
                  </span>
                </div>

                {/* Match Cards List */}
                <div className="flex flex-col justify-around flex-1 space-y-6 relative">
                  {roundMatches.map((m, mIdx) => {
                    const isCompleted = m.status === 'completed';
                    const isLive = (m.status as string) === 'in_progress' || (m.status as string) === 'live';
                    const isSelected = selectedMatchId === m.id;

                    const team1Won = isCompleted && m.winnerId === m.team1?.id;
                    const team2Won = isCompleted && m.winnerId === m.team2?.id;

                    return (
                      <div key={m.id} className="relative group">
                        {/* MATCH CARD */}
                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                          onClick={() => {
                            triggerHaptic('light');
                            setSelectedMatchId(isSelected ? null : m.id);
                          }}
                          className={`p-3.5 rounded-2xl border transition-all duration-300 cursor-pointer shadow-xl relative backdrop-blur-md ${
                            isSelected
                              ? 'bg-indigo-900 border-lime-400 ring-2 ring-lime-400/40 shadow-lime-400/20'
                              : isLive
                              ? 'bg-indigo-950/90 border-amber-400/60 ring-1 ring-amber-400/30'
                              : isCompleted
                              ? 'bg-slate-900/90 border-white/15 hover:border-lime-400/50'
                              : 'bg-slate-950/80 border-white/10 opacity-80'
                          }`}
                        >
                          {/* Match Header Tag */}
                          <div className="flex items-center justify-between border-b border-white/10 pb-1.5 mb-2 text-[10px] font-mono">
                            <span className="text-indigo-300 font-bold uppercase">
                              Match #{m.matchNumber} {m.scheduledTime ? `• ${m.scheduledTime}` : ''}
                            </span>
                            {isCompleted && (
                              <span className="px-1.5 py-0.2 bg-emerald-500/20 text-emerald-300 rounded font-black text-[9px] flex items-center gap-0.5">
                                <CheckCircle2 className="w-2.5 h-2.5 text-emerald-400" />
                                FINAL
                              </span>
                            )}
                            {isLive && (
                              <span className="px-1.5 py-0.2 bg-amber-400 text-black rounded font-black text-[9px] animate-pulse flex items-center gap-0.5">
                                <Flame className="w-2.5 h-2.5 fill-black" />
                                LIVE NOW
                              </span>
                            )}
                          </div>

                          {/* Team 1 Slot */}
                          <div
                            onClick={(e) => {
                              if (m.team1 && onSelectTeam) {
                                e.stopPropagation();
                                onSelectTeam(m.team1);
                              }
                            }}
                            className={`p-2 rounded-xl border flex items-center justify-between transition-colors ${
                              team1Won
                                ? 'bg-gradient-to-r from-lime-400/20 via-emerald-400/10 to-transparent border-lime-400 text-white font-black'
                                : 'bg-black/40 border-white/5 text-slate-200'
                            }`}
                          >
                            <div className="flex items-center space-x-2 min-w-0">
                              <span className="w-5 h-5 rounded-lg bg-indigo-900 text-indigo-200 font-mono text-[9px] font-black flex items-center justify-center shrink-0">
                                #{m.team1?.seed || 1}
                              </span>
                              <span className="text-xs truncate font-bold uppercase">
                                {m.team1?.name || 'TBD Slot'}
                              </span>
                              {team1Won && <Crown className="w-3.5 h-3.5 text-amber-300 fill-amber-300 shrink-0" />}
                            </div>
                            <span className={`text-sm font-black font-mono ml-2 ${team1Won ? 'text-lime-400' : 'text-slate-300'}`}>
                              {m.score1 ?? 0}
                            </span>
                          </div>

                          {/* VS Divider */}
                          <div className="text-center my-1 text-[9px] font-mono text-indigo-400 font-black tracking-widest uppercase">
                            VS
                          </div>

                          {/* Team 2 Slot */}
                          <div
                            onClick={(e) => {
                              if (m.team2 && onSelectTeam) {
                                e.stopPropagation();
                                onSelectTeam(m.team2);
                              }
                            }}
                            className={`p-2 rounded-xl border flex items-center justify-between transition-colors ${
                              team2Won
                                ? 'bg-gradient-to-r from-lime-400/20 via-emerald-400/10 to-transparent border-lime-400 text-white font-black'
                                : 'bg-black/40 border-white/5 text-slate-200'
                            }`}
                          >
                            <div className="flex items-center space-x-2 min-w-0">
                              <span className="w-5 h-5 rounded-lg bg-indigo-900 text-indigo-200 font-mono text-[9px] font-black flex items-center justify-center shrink-0">
                                #{m.team2?.seed || 2}
                              </span>
                              <span className="text-xs truncate font-bold uppercase">
                                {m.team2?.name || 'TBD Slot'}
                              </span>
                              {team2Won && <Crown className="w-3.5 h-3.5 text-amber-300 fill-amber-300 shrink-0" />}
                            </div>
                            <span className={`text-sm font-black font-mono ml-2 ${team2Won ? 'text-lime-400' : 'text-slate-300'}`}>
                              {m.score2 ?? 0}
                            </span>
                          </div>

                          {/* Actions Footer */}
                          {isSelected && (
                            <div className="mt-3 pt-2 border-t border-white/10 flex items-center justify-between text-[10px] gap-2">
                              {onEditMatchScore && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onEditMatchScore(m);
                                  }}
                                  className="px-2.5 py-1 bg-lime-400 text-black font-black uppercase rounded-lg shadow hover:bg-lime-300 transition flex items-center space-x-1"
                                >
                                  <Edit3 className="w-3 h-3 stroke-[2.5]" />
                                  <span>Update Score</span>
                                </button>
                              )}

                              {isCompleted && onNominateMvp && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onNominateMvp(m);
                                  }}
                                  className="px-2.5 py-1 bg-amber-400 text-black font-black uppercase rounded-lg shadow hover:bg-amber-300 transition flex items-center space-x-1"
                                >
                                  <Crown className="w-3 h-3 fill-black" />
                                  <span>MVP Vote</span>
                                </button>
                              )}
                            </div>
                          )}
                        </motion.div>

                        {/* VISUAL CONNECTOR BRANCH LINE TO NEXT ROUND */}
                        {!isFinalRound && (
                          <div className="hidden sm:block absolute top-1/2 left-full w-8 h-1 -translate-y-1/2 pointer-events-none z-10">
                            <div className={`w-full h-full rounded-full transition-colors duration-500 ${
                              isCompleted ? 'bg-gradient-to-r from-lime-400 to-emerald-400 shadow-[0_0_8px_rgba(163,230,53,0.8)]' : 'bg-white/20'
                            }`} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* CHAMPIONSHIP PODIUM POD */}
          <div className="flex flex-col items-center justify-center space-y-4 w-64 shrink-0 border-l border-amber-400/30 pl-8 my-auto text-center">
            <div className="p-5 bg-gradient-to-b from-amber-500/20 via-indigo-900/90 to-slate-950 rounded-3xl border-2 border-amber-400 shadow-[0_0_40px_rgba(245,158,11,0.3)] space-y-3 w-full">
              <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-amber-400 to-yellow-300 mx-auto flex items-center justify-center text-black shadow-xl shadow-amber-400/40 animate-pulse">
                <Trophy className="w-8 h-8 fill-black" />
              </div>

              <div>
                <span className="text-[10px] font-black uppercase text-amber-300 tracking-wider block">
                  CHAMPIONSHIP TROPHY
                </span>
                <h4 className="text-base font-black italic uppercase text-white mt-1">
                  {championTeam ? championTeam.name : 'Winner TBD'}
                </h4>
                <p className="text-[11px] text-lime-300 font-extrabold mt-1">
                  Prize Pool: {tournament.prizePool || '$1,500'}
                </p>
              </div>

              {championTeam ? (
                <div className="p-2.5 bg-amber-400/20 rounded-2xl border border-amber-400/50 text-[10px] font-black uppercase text-amber-300 flex items-center justify-center space-x-1.5">
                  <Sparkles className="w-4 h-4 text-amber-300 fill-amber-300" />
                  <span>Crown Champions! 🎉</span>
                </div>
              ) : (
                <div className="p-2 bg-black/40 rounded-xl border border-white/10 text-[10px] text-indigo-200/80 font-mono italic">
                  Winner advances to claim trophy & bragging rights.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
